import React, { useState, useEffect } from 'react';
import { Card, Button, Switch, Alert } from '../../../components/ui';
import { useNotifications } from '../../../hooks/useNotifications';
import { 
  BellIcon, 
  BellSlashIcon,
  CalendarIcon,
  XCircleIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';

/**
 * Componente para configurações de notificações push
 * Permite ao usuário ativar/desativar diferentes tipos de notificações
 */
const NotificationSettings = () => {
  const { 
    isSupported, 
    permissionStatus, 
    preferences, 
    loading, 
    error, 
    requestPermission, 
    updatePreferences 
  } = useNotifications();
  
  const [localPreferences, setLocalPreferences] = useState({
    newAppointments: true,
    canceledAppointments: true,
    rescheduledAppointments: true,
    appointmentReminders: true
  });
  
  const [saveStatus, setSaveStatus] = useState({
    loading: false,
    success: false,
    error: null
  });

  // Atualizar preferências locais quando as preferências do hook forem carregadas
  useEffect(() => {
    if (preferences) {
      setLocalPreferences(preferences);
    }
  }, [preferences]);

  // Função para lidar com alterações nas preferências
  const handleTogglePreference = (key) => {
    setLocalPreferences(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  // Função para salvar preferências
  const handleSavePreferences = async () => {
    setSaveStatus({
      loading: true,
      success: false,
      error: null
    });

    try {
      const success = await updatePreferences(localPreferences);
      
      if (success) {
        setSaveStatus({
          loading: false,
          success: true,
          error: null
        });
        
        // Limpar o status de sucesso após 3 segundos
        setTimeout(() => {
          setSaveStatus(prev => ({
            ...prev,
            success: false
          }));
        }, 3000);
      } else {
        setSaveStatus({
          loading: false,
          success: false,
          error: 'Não foi possível salvar as preferências.'
        });
      }
    } catch (err) {
      setSaveStatus({
        loading: false,
        success: false,
        error: 'Erro ao salvar preferências: ' + err.message
      });
    }
  };

  // Função para solicitar permissão para notificações
  const handleRequestPermission = async () => {
    await requestPermission();
  };

  // Renderizar mensagem se notificações não forem suportadas
  if (!isSupported) {
    return (
      <Card className="p-6">
        <div className="flex flex-col items-center justify-center text-center gap-4">
          <BellSlashIcon className="h-12 w-12 text-gray-400" />
          <h3 className="text-lg font-medium text-gray-800">Notificações não suportadas</h3>
          <p className="text-gray-600">
            Este dispositivo ou navegador não suporta notificações push.
            Tente acessar em um navegador moderno como Chrome, Firefox ou Safari.
          </p>
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Status de permissão */}
      <Card className="p-6">
        <div className="flex items-start gap-4">
          <div className={`flex-shrink-0 p-2 rounded-full ${
            permissionStatus === 'granted' 
              ? 'bg-green-100 text-green-600' 
              : permissionStatus === 'denied'
                ? 'bg-red-100 text-red-600'
                : 'bg-yellow-100 text-yellow-600'
          }`}>
            <BellIcon className="h-6 w-6" />
          </div>
          
          <div className="flex-1">
            <h3 className="text-lg font-medium text-gray-800">Status de Notificações</h3>
            <p className="text-gray-600 mb-4">
              {permissionStatus === 'granted' 
                ? 'Você receberá notificações push sobre seus agendamentos.'
                : permissionStatus === 'denied'
                  ? 'As notificações estão bloqueadas para este site. Para recebê-las, você precisa alterar as permissões nas configurações do navegador.'
                  : 'Para receber notificações sobre seus agendamentos, conceda permissão usando o botão abaixo.'}
            </p>
            
            {permissionStatus !== 'granted' && permissionStatus !== 'denied' && (
              <Button 
                onClick={handleRequestPermission} 
                disabled={loading}
                className="flex items-center gap-2"
              >
                <BellIcon className="h-5 w-5" />
                Ativar Notificações
              </Button>
            )}
          </div>
        </div>
      </Card>
      
      {/* Preferências de notificação - apenas mostrar se as permissões foram concedidas */}
      {permissionStatus === 'granted' && (
        <Card className="p-6">
          <h3 className="text-lg font-medium text-gray-800 mb-4">Configurações de Notificações</h3>
          
          {/* Mensagem de status */}
          {saveStatus.success && (
            <Alert type="success" title="Configurações salvas com sucesso!" className="mb-4" />
          )}
          
          {saveStatus.error && (
            <Alert type="error" title={saveStatus.error} className="mb-4" />
          )}
          
          {error && (
            <Alert type="error" title={error} className="mb-4" />
          )}
          
          <div className="space-y-4">
            {/* Notificação de novo agendamento */}
            <div className="flex items-center justify-between py-2 border-b border-gray-200">
              <div className="flex items-center gap-3">
                <div className="p-1.5 bg-blue-100 text-blue-600 rounded-full">
                  <CalendarIcon className="h-5 w-5" />
                </div>
                <div>
                  <p className="font-medium text-gray-800">Novos agendamentos</p>
                  <p className="text-sm text-gray-500">Receba notificações quando um cliente fizer um novo agendamento</p>
                </div>
              </div>
              <Switch 
                checked={localPreferences.newAppointments} 
                onChange={() => handleTogglePreference('newAppointments')}
              />
            </div>
            
            {/* Notificação de cancelamento */}
            <div className="flex items-center justify-between py-2 border-b border-gray-200">
              <div className="flex items-center gap-3">
                <div className="p-1.5 bg-red-100 text-red-600 rounded-full">
                  <XCircleIcon className="h-5 w-5" />
                </div>
                <div>
                  <p className="font-medium text-gray-800">Cancelamentos</p>
                  <p className="text-sm text-gray-500">Receba notificações quando um cliente cancelar um agendamento</p>
                </div>
              </div>
              <Switch 
                checked={localPreferences.canceledAppointments} 
                onChange={() => handleTogglePreference('canceledAppointments')}
              />
            </div>
            
            {/* Notificação de remarcação */}
            <div className="flex items-center justify-between py-2 border-b border-gray-200">
              <div className="flex items-center gap-3">
                <div className="p-1.5 bg-yellow-100 text-yellow-600 rounded-full">
                  <ArrowPathIcon className="h-5 w-5" />
                </div>
                <div>
                  <p className="font-medium text-gray-800">Remarcações</p>
                  <p className="text-sm text-gray-500">Receba notificações quando um cliente remarcar um agendamento</p>
                </div>
              </div>
              <Switch 
                checked={localPreferences.rescheduledAppointments} 
                onChange={() => handleTogglePreference('rescheduledAppointments')}
              />
            </div>

            {/* Lembretes de agendamento */}
            <div className="flex items-center justify-between py-2 border-b border-gray-200">
              <div className="flex items-center gap-3">
                <div className="p-1.5 bg-green-100 text-green-600 rounded-full">
                  <BellIcon className="h-5 w-5" />
                </div>
                <div>
                  <p className="font-medium text-gray-800">Lembretes</p>
                  <p className="text-sm text-gray-500">Receba lembretes um dia antes dos agendamentos</p>
                </div>
              </div>
              <Switch 
                checked={localPreferences.appointmentReminders} 
                onChange={() => handleTogglePreference('appointmentReminders')}
              />
            </div>
          </div>
          
          <div className="mt-6">
            <Button 
              onClick={handleSavePreferences} 
              disabled={loading || saveStatus.loading}
              className="w-full sm:w-auto"
            >
              {saveStatus.loading ? 'Salvando...' : 'Salvar Preferências'}
            </Button>
          </div>
        </Card>
      )}
    </div>
  );
};

export default NotificationSettings; 